import { computed, ref, watch, type ComputedRef } from 'vue'
import { storeToRefs } from 'pinia'

import { useCalculationStore } from '@/stores/calculation'
import { useBuildingBlockStore } from '@/stores/buildingBlock'
import { useMaterialStore } from '@/stores/material'
import { useActivityStore } from '@/stores/activity'
import { useProductStore } from '@/stores/product'
import { useTransportConfigStore } from '@/stores/transportConfig'
import { useEnvironmentDatabaseStore } from '@/stores/environmentDatabase'
import { useQuantityBuildingBlockStore } from '@/stores/quantityBuildingBlock'
import { useCalculationEnvironmentDatabaseSelectionStore } from '@/stores/calculationEnvironmentDatabaseSelection'
import type {
  BlockSwitchIssueMap,
  SwitchValidationResult
} from '@/components/building-block-form/utils/switchValidation'
import type { BuildingBlockDto } from '@/stores/buildingBlock'
import type { TransportConfig } from '@/stores/type/transportConfig.type'
import {
  hasChangedSwitchIssues,
  normalizeDatabaseId,
  resolveDatabaseNameById,
  toBlockSwitchIssueMap
} from '@/views/building_blocks/utils/buildingBlockViewUtils'
import {
  buildAffectedBuildingBlocks,
  buildSelectionSwitchNoticeData,
  buildTransportConfigMissingRows,
  collectSelectedBuildingBlockIds,
  collectTransportConfigBuildingBlockIds
} from '@/components/calculation-form/utils/calculationDatabaseSwitch'
import type { AffectedBuildingBlockSummary } from '@/components/calculation-form/utils/calculationDatabaseSwitch'

const switchValidationByBlockState = ref<BlockSwitchIssueMap>({})
const switchChangedHighlightBaselineState = ref<string | null>(null)
const operationLoadingVisibleState = ref(false)
const operationLoadingMessageState = ref('')
const activeCalculationIdState = ref<number | null>(null)
const showSwitchValidationSummaryDialogState = ref(false)
const affectedBuildingBlocksState = ref<AffectedBuildingBlockSummary[]>([])
const switchBaseDatabaseNameState = ref('')
const switchTargetDatabaseNameState = ref('')

export function useCalculationDatabaseSwitch({
  isReadOnly,
  projectId,
  calculationId,
  customDataSignature
}: {
  isReadOnly: ComputedRef<boolean>
  projectId: ComputedRef<number>
  calculationId: ComputedRef<number>
  customDataSignature?: ComputedRef<string>
}) {
  const calculationStore = useCalculationStore()
  const { currentItem } = storeToRefs(calculationStore)

  const buildingBlockStore = useBuildingBlockStore()
  const materialStore = useMaterialStore()
  const activityStore = useActivityStore()
  const productStore = useProductStore()
  const transportConfigStore = useTransportConfigStore()
  const environmentDatabaseStore = useEnvironmentDatabaseStore()
  const quantityBuildingBlockStore = useQuantityBuildingBlockStore()
  const environmentDatabaseSelectionStore = useCalculationEnvironmentDatabaseSelectionStore()

  const ensureCalculationScope = () => {
    const currentCalculationId = calculationId.value || null
    if (currentCalculationId === activeCalculationIdState.value) return

    activeCalculationIdState.value = currentCalculationId
    switchValidationByBlockState.value = {}
    switchChangedHighlightBaselineState.value = null
    operationLoadingVisibleState.value = false
    operationLoadingMessageState.value = ''
    showSwitchValidationSummaryDialogState.value = false
    affectedBuildingBlocksState.value = []
    switchBaseDatabaseNameState.value = ''
    switchTargetDatabaseNameState.value = ''
  }

  const clearSwitchValidationSummary = () => {
    showSwitchValidationSummaryDialogState.value = false
    affectedBuildingBlocksState.value = []
    switchBaseDatabaseNameState.value = ''
    switchTargetDatabaseNameState.value = ''
  }

  const closeSwitchValidationSummaryDialog = () => {
    showSwitchValidationSummaryDialogState.value = false
  }

  const selectedDatabase = computed<number | null>({
    get: () => {
      const calculationDatabase = normalizeDatabaseId(currentItem.value?.database)
      if (calculationDatabase !== null) return calculationDatabase
      return normalizeDatabaseId(
        environmentDatabaseSelectionStore.getSelectedDatabase(calculationId.value)
      )
    },
    set: (value) => {
      if (!calculationId.value) return
      environmentDatabaseSelectionStore.setSelectedDatabase(
        calculationId.value,
        normalizeDatabaseId(value)
      )
    }
  })

  const hasValidDatabaseSelection = computed(() => {
    const selectedDatabaseId = normalizeDatabaseId(selectedDatabase.value)
    if (!selectedDatabaseId) return false

    return environmentDatabaseStore.items.some(
      (database) => Number(database.id) === selectedDatabaseId
    )
  })

  const hasSelectedDatabase = computed(() => hasValidDatabaseSelection.value)

  const selectedDatabaseName = computed(() => {
    const selectedDatabaseId = normalizeDatabaseId(selectedDatabase.value)
    if (selectedDatabaseId === null) return ''

    return resolveDatabaseNameById(environmentDatabaseStore.items, selectedDatabaseId, '')
  })

  const isBlockedByMissingIssues = computed(() =>
    Object.values(switchValidationByBlockState.value).some((issue) => issue.missingRows.length > 0)
  )

  const openOperationLoading = (message: string) => {
    operationLoadingMessageState.value = message
    operationLoadingVisibleState.value = true
  }

  const closeOperationLoading = () => {
    operationLoadingVisibleState.value = false
    operationLoadingMessageState.value = ''
  }

  const clearSwitchHighlights = () => {
    switchValidationByBlockState.value = {}
    switchChangedHighlightBaselineState.value = null
    clearSwitchValidationSummary()
    calculationStore.setHasMissingDatabaseSwitchIssues(false)
    calculationStore.setDesignLinkSwitchNotice(null)
    calculationStore.setBuildingBlockSelectionSwitchNotice(null)
    calculationStore.setTransportConfigSwitchNotice(null)
  }

  const onDatabaseChange = async (newId: number | null): Promise<boolean> => {
    ensureCalculationScope()

    if (isReadOnly.value) return false

    const nextDatabaseId = normalizeDatabaseId(newId)
    const persistedDatabaseId = normalizeDatabaseId(currentItem.value?.database)
    if (nextDatabaseId === persistedDatabaseId) return false

    const previousCalculation = currentItem.value ? { ...currentItem.value } : null
    selectedDatabase.value = nextDatabaseId

    if (!currentItem.value) return false

    const targetDatabaseName = resolveDatabaseNameById(
      environmentDatabaseStore.items,
      nextDatabaseId,
      'selected database'
    )
    openOperationLoading(`Switching to ${targetDatabaseName}`)

    try {
      clearSwitchHighlights()
      let switchNotice: SwitchValidationResult | null = null
      let nextSwitchBaseDatabaseName: string | undefined
      let nextSwitchTargetDatabaseName: string | undefined

      if (persistedDatabaseId !== null && nextDatabaseId !== null && currentItem.value.id) {
        const validationResult = await calculationStore.validateSwitchDatabase(
          currentItem.value.id,
          projectId.value,
          nextDatabaseId
        )

        if (!validationResult) {
          selectedDatabase.value = persistedDatabaseId
          return false
        }

        const missingCount = validationResult?.summary?.missing_rows ?? 0
        const changedCount = validationResult?.summary?.changed_rows ?? 0

        if (missingCount > 0 || changedCount > 0) {
          switchNotice = validationResult
        }

        nextSwitchBaseDatabaseName = validationResult?.base_database?.name
        nextSwitchTargetDatabaseName = validationResult?.target_database?.name

        const designLinkMissingRows = validationResult?.design_link_missing_rows ?? []
        const designLinkChangedRows = validationResult?.design_link_changed_rows ?? []
        if (designLinkMissingRows.length || designLinkChangedRows.length) {
          calculationStore.setDesignLinkSwitchNotice({
            baseDatabaseName: validationResult?.base_database?.name,
            targetDatabaseName: validationResult?.target_database?.name,
            missingRows: designLinkMissingRows,
            changedRows: designLinkChangedRows
          })
          calculationStore.setSnackbarMessage(
            'Some Design Links are affected by the database switch. Please review the Design Links tab.',
            'warning'
          )
        } else {
          calculationStore.setDesignLinkSwitchNotice(null)
        }

        const selectedBlockIds = collectSelectedBuildingBlockIds(
          (buildingBlockStore.items || []) as BuildingBlockDto[],
          calculationId.value
        )
        const selectionNoticeData = buildSelectionSwitchNoticeData({
          validationResult,
          selectedBlockIds,
          designLinkMissingRows,
          designLinkChangedRows
        })

        if (selectionNoticeData.hasIssues) {
          calculationStore.setBuildingBlockSelectionSwitchNotice({
            baseDatabaseName: nextSwitchBaseDatabaseName,
            targetDatabaseName: nextSwitchTargetDatabaseName,
            missingBlocks: selectionNoticeData.missingBlocks,
            changedBlocks: selectionNoticeData.changedBlocks
          })
          calculationStore.setSnackbarMessage(
            'Some Building Block Selection defaults were affected by the database switch.',
            'warning'
          )
        } else {
          calculationStore.setBuildingBlockSelectionSwitchNotice(null)
        }

        const transportConfigBlockIds = collectTransportConfigBuildingBlockIds(
          (transportConfigStore.items || []) as TransportConfig[],
          calculationId.value
        )

        const transportMissingRows = buildTransportConfigMissingRows({
          validationResult,
          designLinkMissingRows,
          transportConfigBlockIds
        })

        if (transportMissingRows.length) {
          calculationStore.setTransportConfigSwitchNotice({
            baseDatabaseName: nextSwitchBaseDatabaseName,
            targetDatabaseName: nextSwitchTargetDatabaseName,
            missingRows: transportMissingRows
          })
          calculationStore.setSnackbarMessage(
            'Some Transport Configuration rows were removed because their building blocks are missing in the selected database.',
            'warning'
          )
        } else {
          calculationStore.setTransportConfigSwitchNotice(null)
        }
      }

      const updatedCalculation = {
        ...currentItem.value,
        database: nextDatabaseId
      }
      calculationStore.setCurrentItem(updatedCalculation)
      await calculationStore.update(updatedCalculation)

      if (calculationId.value) {
        await Promise.all([
          buildingBlockStore.fetchItems({ calculation: calculationId.value }),
          materialStore.fetchItems({ block__calculation: calculationId.value }),
          activityStore.fetchItems({ material__block__calculation: calculationId.value }),
          quantityBuildingBlockStore.fetchItems({ calculation: calculationId.value }),
          transportConfigStore.fetchItems({ calculation: calculationId.value }),
          ...(nextDatabaseId
            ? [productStore.fetchItems({ environment_database: nextDatabaseId })]
            : [])
        ])
      }

      if (switchNotice) {
        switchValidationByBlockState.value = toBlockSwitchIssueMap(switchNotice)
        affectedBuildingBlocksState.value = buildAffectedBuildingBlocks(
          switchValidationByBlockState.value
        )
        switchBaseDatabaseNameState.value = nextSwitchBaseDatabaseName || ''
        switchTargetDatabaseNameState.value = nextSwitchTargetDatabaseName || ''
        showSwitchValidationSummaryDialogState.value = affectedBuildingBlocksState.value.length > 0
        switchChangedHighlightBaselineState.value = hasChangedSwitchIssues(
          switchValidationByBlockState.value
        )
          ? (customDataSignature?.value ?? null)
          : null
      } else {
        clearSwitchValidationSummary()
      }
      calculationStore.setHasMissingDatabaseSwitchIssues(isBlockedByMissingIssues.value)
      return true
    } catch (error) {
      if (previousCalculation) {
        calculationStore.setCurrentItem(previousCalculation)
        selectedDatabase.value = normalizeDatabaseId(previousCalculation.database)
      }
      clearSwitchHighlights()
      console.error('Failed to update calculation database selection:', error)
      return false
    } finally {
      closeOperationLoading()
    }
  }

  watch(
    calculationId,
    () => {
      ensureCalculationScope()
    },
    { immediate: true }
  )

  return {
    selectedDatabase,
    hasValidDatabaseSelection,
    hasSelectedDatabase,
    selectedDatabaseName,
    switchValidationByBlock: switchValidationByBlockState,
    switchChangedHighlightBaseline: switchChangedHighlightBaselineState,
    operationLoadingVisible: operationLoadingVisibleState,
    operationLoadingMessage: operationLoadingMessageState,
    showSwitchValidationSummaryDialog: showSwitchValidationSummaryDialogState,
    affectedBuildingBlocks: affectedBuildingBlocksState,
    switchBaseDatabaseName: switchBaseDatabaseNameState,
    switchTargetDatabaseName: switchTargetDatabaseNameState,
    isBlockedByMissingIssues,
    clearSwitchHighlights,
    closeSwitchValidationSummaryDialog,
    onDatabaseChange
  }
}
